import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import LocalActivityIcon from '@material-ui/icons/LocalActivity';
import { withStyles } from '@material-ui/core/styles';
import { formatCostPrice } from 'utils/common';
import PropTypes from 'prop-types';
import * as _ from 'lodash';
import React, { Component } from 'react';

class ActivityCard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      currentImageIndex: 0,
      maxDots: 5,
    };
  }

  getImages = () => {
    const { itineraryPart = {} } = this.props;
    return _.isEmpty(itineraryPart.activityData?.images)
      ? []
      : itineraryPart.activityData.images.map((img) => encodeURI(img));
  };

  nextImage = () => {
    const { maxDots } = this.state;
    const images = this.getImages();
    const length = Math.min(images.length, maxDots);
    this.setState((prevState) => ({
      currentImageIndex: (prevState.currentImageIndex + 1) % length,
    }));
  };

  prevImage = () => {
    const { maxDots } = this.state;
    const images = this.getImages();
    const length = Math.min(images.length, maxDots);
    this.setState((prevState) => ({
      currentImageIndex: (prevState.currentImageIndex - 1 + length) % length,
    }));
  };

  render() {
    const { onViewGallery, classes, itineraryPart = {} } = this.props;
    const { currentImageIndex, maxDots } = this.state;
    const {
      activityData = {}, costPrice, manualCostPrice, fxCostPrice, fxCode, isFree,
    } = itineraryPart;

    const images = this.getImages();    

    // Limit pagination dots to 5 maximum
    const totalImages = images.length;
    const showDots = Math.min(totalImages, maxDots);

    return (
      <div className={classes.cardContainer}>
        {/* Left Section - Image Gallery */}
        <div
          role="button"
          tabIndex={0}
          className={`${classes.imageGallery} ${images.length > 0 ? classes.imageGalleryWithImages : ''}`}
          style={images.length > 0 ? {
            backgroundImage: `url("${images[currentImageIndex]}")`,
          } : {}}
          onClick={() => {
            if (onViewGallery && images.length > 0) {
              onViewGallery(images, currentImageIndex);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              if (onViewGallery && images.length > 0) {
                onViewGallery(images, currentImageIndex);
              }
            }
          }}
        >
          {images.length === 0 && (
            <div className={classes.noImagePlaceholder}>
              <LocalActivityIcon className={classes.activityIcon} />
            </div>
          )}

          {/* Navigation arrows - only show if multiple images */}
          {totalImages > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  this.prevImage();
                }}
                className={`${classes.navButton} ${classes.navButtonLeft}`}
              >
                <ChevronLeftIcon className={classes.navIcon} />
              </button>

              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  this.nextImage();
                }}
                className={`${classes.navButton} ${classes.navButtonRight}`}
              >
                <ChevronRightIcon className={classes.navIcon} />
              </button>

              {/* Pagination dots - limited to 5 */}
              <div className={classes.paginationDots}>
                {Array.from({ length: showDots }, (dot, index) => (
                  <div
                    key={`dot-${index}`}
                    className={`${classes.dot} ${index === (currentImageIndex % showDots) ? classes.dotActive : classes.dotInactive}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        {/* Right Section - Activity Details */}
        <div className={classes.detailsSection}>
          {/* Top section - Title and description aligned to top */}
          <div className={classes.topSection}>
            {/* Activity name - truncated with hover */}
            <div
              className={classes.activityName}
              title="experienceName"
            >
              {activityData.experienceName}
            </div>

            {/* Description - only show if available, max 2 lines */}
            {activityData.shortDescription && (
              <p
                className={classes.activityDescription}
                title="shortDescription"
              >
                {activityData.shortDescription}
              </p>
            )}

            {/* Separator line - stays in same position */}
            <div className={classes.separator} />
          </div>

          {/* Bottom section - Activity details and price */}
          <div className={classes.bottomSection}>
            {/* Activity details from API */}
            <div className={classes.detailsList}>
              {activityData.details?.map((detail, index) => (
                <div
                  key={detail}
                  className={`${classes.detailItem} ${index < (activityData.details?.length || 0) - 1 ? classes.detailItemMargin : ''}`}
                  title={detail}
                >
                  {detail}
                </div>
              ))}
            </div>
          
            {/* Price display */}
            {
              (isFree || (costPrice || manualCostPrice) > 0)
                ? (
                  <div className={classes.priceValid}>
                    {
                      isFree
                        ? '(FREE)'
                        : (itineraryPart.manualCostPrice > 0)
                          ? formatCostPrice(itineraryPart.manualCostPrice)
                          : formatCostPrice(itineraryPart.costPrice, itineraryPart.fxCostPrice, itineraryPart.fxCode)
                    }
                  </div>
                )
                : (
                  <div className={classes.priceMissing}>
                    PRICE MISSING
                  </div>
                )
            }
          </div>      
        </div>
      </div>
    );
  }
}

const styles = (theme) => ({
  cardContainer: {
    minHeight: 200,
    backgroundColor: theme.colors.white,
    borderRadius: 12,
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.3), 0px 4px 8px 3px rgba(0, 0, 0, 0.15)',
    display: 'flex',
    overflow: 'hidden',
    fontFamily: 'Sofia-pro, sans-serif',
  },
  imageGallery: {
    width: 267,
    minHeight: 200,
    position: 'relative',
  },
  imageGalleryWithImages: {
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    cursor: 'pointer',
  },
  noImagePlaceholder: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
  },
  activityIcon: {
    fontSize: 120,
    color: theme.colors.border,
  },
  navButton: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    width: 24,
    height: 24,
    borderRadius: '50%',
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  navButtonLeft: {
    left: 16,
  },
  navButtonRight: {
    right: 16,
  },
  navIcon: {
    fontSize: 20,
    color: theme.colors.black,
  },
  paginationDots: {
    position: 'absolute',
    bottom: 16,
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    gap: 3,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
  },
  dotActive: {
    backgroundColor: '#fff',
    border: 'none',
  },
  dotInactive: {
    backgroundColor: 'transparent',
    border: '0.5px solid #fff',
  },
  detailsSection: {
    flex: 1,
    minWidth: 0,
    padding: '16px 16px 16px 16px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  topSection: {
    minWidth: 0,
  },
  activityName: {
    fontSize: 16,
    fontWeight: 700,
    color: '#32302C',
    margin: 0,
    marginBottom: 8,
  },
  activityDescription: {
    fontSize: 14,
    color: '#32302C',
    margin: 0,
    marginBottom: 16,
    lineHeight: '1.5',
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    textWrap: 'auto',
  },
  separator: {
    height: 0.5,
    backgroundColor: theme.colors.border,
    marginBottom: 12,
  },
  bottomSection: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    width: '100%',
  },
  detailsList: {
    minWidth: 0,
    flex: 1,
  },
  detailItem: {
    fontSize: 14,
    color: theme.colors.textLight,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  detailItemMargin: {
    marginBottom: 8,
  },
  priceValid: {
    fontSize: 14,
    color: theme.colors.green,
    fontWeight: 400,
    paddingLeft: 16,
  },
  priceMissing: {
    fontSize: 14,
    color: theme.colors.red,
    fontWeight: 400,
    paddingLeft: 16,
  },
});

ActivityCard.propTypes = {
  onViewGallery: PropTypes.func,
  classes: PropTypes.object.isRequired,
  itineraryPart: PropTypes.shape({
    itineraryPartId: PropTypes.string,
    type: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    startTime: PropTypes.string,
    endTime: PropTypes.string,
    activityData: PropTypes.shape({
      experienceId: PropTypes.string,
      experienceName: PropTypes.string,
      duration: PropTypes.string,
      isPrivateTransfer: PropTypes.bool,
      isSicTransfer: PropTypes.bool,
      adults: PropTypes.number,
      children: PropTypes.number,
      infants: PropTypes.number,
      images: PropTypes.arrayOf(PropTypes.string),
      details: PropTypes.arrayOf(PropTypes.string),
    }),
    manualCostPrice: PropTypes.number,
    costPrice: PropTypes.number,
    fxCostPrice: PropTypes.number,
    fxCode: PropTypes.string,
  }),
};

export default withStyles(styles)(ActivityCard); 
